import { useParams } from "react-router-dom";
import type { BoardItemProjection, BoardStatus, MemberProjection } from "../domain";
import { formatDateTime } from "../lib/time";
import { useRemote } from "../state/RemoteContext";

const groups: Array<{ status: BoardStatus; label: string }> = [
  { status: "blocked", label: "Blocked" },
  { status: "next", label: "Next" },
  { status: "in-progress", label: "In progress" },
  { status: "review", label: "Review" },
  { status: "backlog", label: "Backlog" },
  { status: "done", label: "Done recently" },
];

export function boardAssigneeLabel(
  assigneeId: string | null,
  members: ReadonlyArray<Pick<MemberProjection, "id" | "name">>,
) {
  if (!assigneeId) return "Unassigned";
  return members.find((member) => member.id === assigneeId)?.name ?? assigneeId;
}

export function boardHasSupportedEdits(
  items: ReadonlyArray<Pick<BoardItemProjection, "remoteEditableFields">>,
) {
  return items.some((item) => item.remoteEditableFields.some((field) => field !== "checkpoint"));
}

export function BoardScreen() {
  const { crewId } = useParams();
  const { fixture, setDialog, can, canMutate, mutationBlockReason } = useRemote();
  const crew = fixture.crews.find((candidate) => candidate.id === crewId);
  if (!crew) return <div className="screen empty-state"><h1>Board unavailable</h1></div>;
  const authorityAdvertisesEdits = boardHasSupportedEdits(crew.board);
  const editable = can("board.edit") && canMutate && authorityAdvertisesEdits;

  return (
    <div className="screen">
      <header className="screen-heading">
        <span className="eyebrow">Bounded operational fields · {editable ? "editable" : "read-only"}</span>
        <h1>Board</h1>
        <p>Descriptions, acceptance criteria and dependencies stay on desktop.</p>
        {can("board.edit") && !canMutate && <p className="permission-note">{mutationBlockReason}</p>}
        {can("board.edit") && canMutate && !authorityAdvertisesEdits && <p className="permission-note">The authority did not advertise a supported editable field.</p>}
      </header>
      <div className="board-groups">
        {groups.map((group) => {
          const items = crew.board.filter((item) => item.status === group.status);
          return (
            <section className="board-group" data-status={group.status} key={group.status}>
              <h2>{group.label} <span>{items.length}</span></h2>
              {items.length === 0 && <p className="board-empty">No items</p>}
              {items.map((item) => (
                <button type="button" className="board-card" key={item.id} onClick={() => setDialog({ type: "board", crewId: crew.id, itemId: item.id })}>
                  <small>{item.id} · {item.priority} · r{item.revision}</small>
                  <strong>{item.title}</strong>
                  <span>{boardAssigneeLabel(item.assigneeId, crew.members)}{item.checkpointAt ? ` · ${formatDateTime(item.checkpointAt)}` : ""}</span>
                </button>
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}
